import {
    calculatorFishingSourceTraces,
    calculatorTraceMatchesFishingSource,
} from "./calculator-layer-inspector-contract.js";
import { calculatorFishingSourcesEqual } from "./calculator-fishing-source.js";
import { calculatorDataText as t } from "./calculator-data-i18n.js";

const RANKING_SCOPES = Object.freeze({
    rankingFullyContained: Object.freeze({
        key: "rankingFullyContained",
        labelKey: "calculator.data.evidence.scope.fully_contained",
        labelFallback: "Fully contained ranking rings",
        tone: "success",
    }),
    rankingPartialIntersection: Object.freeze({
        key: "rankingPartialIntersection",
        labelKey: "calculator.data.evidence.scope.partial_intersection",
        labelFallback: "Boundary-intersecting ranking rings",
        tone: "warning",
    }),
});

const CANDIDATE_STATUSES = Object.freeze([
    "effective",
    "overridden",
    "refuted",
    "inactiveCondition",
]);

function isRecord(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function itemLabel(presentation, itemId) {
    const value = presentation?.itemsByItemId?.[String(itemId)];
    return typeof value?.label === "string" && value.label.trim()
        ? value.label.trim()
        : t("calculator.data.item_id", "Item { $id }", { id: itemId });
}

function groupLabel(presentation, groupId) {
    const label = presentation?.groupsByGroupId?.[groupId]?.label;
    return typeof label === "string" && label.trim() ? label.trim() : groupId;
}

function effectiveCandidate(trace) {
    return trace?.candidates?.find((candidate) => candidate.status === "effective") ?? null;
}

function typedAssignmentRate(projection, itemId, groupId) {
    const quantified = projection.sankey?.items?.find((row) => (
        row.itemId === itemId && row.groupId === groupId
    ));
    if (quantified) {
        const rate = quantified.rate;
        if (
            !isRecord(rate)
            || typeof rate.value !== "number"
            || !Number.isFinite(rate.value)
            || rate.value < 0
            || !["ratio", "percent", "rawWeight", "perCatch"].includes(rate.basis)
        ) {
            throw new TypeError(`quantified item rate for ${itemId}/${groupId} is invalid`);
        }
        return Object.freeze({
            state: "known",
            value: rate.value,
            basis: rate.basis,
        });
    }
    const rate = projection.unquantifiedItems?.find((row) => (
        row.itemId === itemId && row.groupId === groupId
    ))?.rate;
    if (rate === undefined) {
        return Object.freeze({ state: "unknown" });
    }
    if (!isRecord(rate) || !["known", "unknown", "unavailable"].includes(rate.state)) {
        throw new TypeError(`item rate for ${itemId}/${groupId} is not a typed rate`);
    }
    if (
        rate.state === "known"
        && (typeof rate.value !== "number" || !Number.isFinite(rate.value) || rate.value < 0)
    ) {
        throw new TypeError(`known item rate for ${itemId}/${groupId} must be non-negative and finite`);
    }
    return rate;
}

function effectiveAssignments(projection, fishingSource, itemId, presentation) {
    const assignments = [];
    for (const trace of calculatorFishingSourceTraces(projection, fishingSource)) {
        const factKey = trace.factKey;
        if (factKey?.kind !== "groupMembership" || factKey.itemId !== itemId) {
            continue;
        }
        const candidate = effectiveCandidate(trace);
        if (candidate?.operation?.membership !== "member") {
            continue;
        }
        assignments.push(Object.freeze({
            groupId: factKey.groupId,
            groupLabel: groupLabel(presentation, factKey.groupId),
            traceId: trace.traceId,
            factId: trace.effectiveFactId,
            layerId: candidate.layer.layerId,
            layerName: candidate.layer.name,
            layerKind: candidate.layer.kind,
            evidenceClass: candidate.layer.evidenceClass,
            sourceId: candidate.operation.provenance?.sourceId ?? null,
            sourceName: candidate.operation.provenance?.sourceName ?? null,
            rate: typedAssignmentRate(projection, itemId, factKey.groupId),
        }));
    }
    assignments.sort((left, right) => (
        left.groupLabel.localeCompare(right.groupLabel)
        || left.groupId.localeCompare(right.groupId)
    ));
    return Object.freeze(assignments);
}

function rankingObservation(candidate, path) {
    const operation = candidate?.operation;
    const provenance = operation?.provenance;
    const scope = provenance?.observationScope;
    if (scope === null || scope === undefined) {
        return null;
    }
    const definition = RANKING_SCOPES[scope];
    if (!definition) {
        throw new TypeError(`${path}.operation.provenance.observationScope is unsupported`);
    }
    if (operation.kind !== "itemPresence") {
        throw new TypeError(`${path} attaches ranking observation scope to a non-presence fact`);
    }
    if (!Number.isInteger(operation.itemId) || operation.itemId < 1) {
        throw new TypeError(`${path}.operation.itemId must be a positive integer`);
    }
    const count = provenance.observationCount;
    if (!Number.isInteger(count) || count < 1) {
        throw new TypeError(`${path}.operation.provenance.observationCount must be a positive integer`);
    }
    if (!CANDIDATE_STATUSES.includes(candidate.status)) {
        throw new TypeError(`${path}.status is unsupported`);
    }
    return {
        scope,
        label: t(definition.labelKey, definition.labelFallback),
        tone: definition.tone,
        count,
        status: candidate.status,
        observedAtUtc: provenance.observedAtUtc ?? null,
        layerId: candidate.layer.layerId,
        layerName: candidate.layer.name,
        evidenceClass: candidate.layer.evidenceClass,
        sourceId: provenance.sourceId ?? null,
        sourceName: provenance.sourceName ?? null,
        factId: operation.factId ?? null,
        operationId: operation.operationId,
    };
}

function observationKey(observation) {
    return JSON.stringify([
        observation.scope,
        observation.count,
        observation.observedAtUtc,
        observation.sourceId,
        observation.sourceName,
        observation.layerId,
        observation.status,
    ]);
}

function addObservation(row, observation) {
    const key = observationKey(observation);
    const existing = row.observations.get(key);
    if (existing) {
        if (observation.factId) {
            existing.factIds.add(observation.factId);
        }
        existing.operationIds.add(observation.operationId);
        return;
    }
    row.observations.set(key, {
        ...observation,
        factIds: new Set(observation.factId ? [observation.factId] : []),
        operationIds: new Set([observation.operationId]),
    });
}

function freezeObservation(observation) {
    return Object.freeze({
        scope: observation.scope,
        label: observation.label,
        tone: observation.tone,
        count: observation.count,
        status: observation.status,
        observedAtUtc: observation.observedAtUtc,
        layerId: observation.layerId,
        layerName: observation.layerName,
        evidenceClass: observation.evidenceClass,
        sourceId: observation.sourceId,
        sourceName: observation.sourceName,
        factIds: Object.freeze([...observation.factIds].sort()),
        operationIds: Object.freeze([...observation.operationIds].sort()),
    });
}

function observationStatusRank(status) {
    return CANDIDATE_STATUSES.indexOf(status);
}

function freezeObservations(observations) {
    return Object.freeze([...observations.values()]
        .map(freezeObservation)
        .sort((left, right) => (
            observationStatusRank(left.status) - observationStatusRank(right.status)
            || Number(right.scope === "rankingFullyContained")
                - Number(left.scope === "rankingFullyContained")
            || right.count - left.count
            || left.layerName.localeCompare(right.layerName)
            || String(left.sourceName ?? "").localeCompare(String(right.sourceName ?? ""))
            || String(left.sourceId ?? "").localeCompare(String(right.sourceId ?? ""))
        )));
}

function largestEffectiveCount(observations, scope) {
    return observations.reduce((largest, observation) => (
        observation.status === "effective" && observation.scope === scope
            ? Math.max(largest, observation.count)
            : largest
    ), 0);
}

/**
 * Builds the Data-workspace view model exclusively from resolved provenance
 * candidates. Ranking observations support selected-source presence; group assignments
 * remain independent facts and may contain several memberships for one item.
 */
export function buildCalculatorRankingEvidenceRows({
    model,
    projection,
    presentation = null,
}) {
    if (!isRecord(model) || !isRecord(projection)) {
        return Object.freeze([]);
    }
    const rows = new Map();
    const fishingSource = model.selection?.fishingSource;
    for (const [traceIndex, trace] of (projection.provenanceTraces ?? []).entries()) {
        if (
            trace.factKey?.kind !== "itemPresence"
            || !calculatorTraceMatchesFishingSource(trace, fishingSource)
        ) {
            continue;
        }
        for (const [candidateIndex, candidate] of (trace.candidates ?? []).entries()) {
            const observation = rankingObservation(
                candidate,
                `projection.provenanceTraces[${traceIndex}].candidates[${candidateIndex}]`,
            );
            if (!observation) {
                continue;
            }
            const operation = candidate.operation;
            if (
                operation.fishingSource !== undefined
                && !calculatorFishingSourcesEqual(
                    operation.fishingSource,
                    fishingSource,
                )
            ) {
                continue;
            }
            const row = rows.get(operation.itemId) ?? {
                itemId: operation.itemId,
                observations: new Map(),
                presenceTraceIds: new Set(),
            };
            addObservation(row, observation);
            row.presenceTraceIds.add(trace.traceId);
            rows.set(operation.itemId, row);
        }
    }

    const result = [...rows.values()].map((row) => {
        const observations = freezeObservations(row.observations);
        const assignments = effectiveAssignments(
            projection,
            fishingSource,
            row.itemId,
            presentation,
        );
        const presenceTraceIds = Object.freeze([...row.presenceTraceIds].sort());
        return Object.freeze({
            itemId: row.itemId,
            label: itemLabel(presentation, row.itemId),
            presenceTraceId: presenceTraceIds[0] ?? null,
            presenceTraceIds,
            observations,
            effectiveFullyContainedCount: largestEffectiveCount(
                observations,
                "rankingFullyContained",
            ),
            effectivePartialIntersectionCount: largestEffectiveCount(
                observations,
                "rankingPartialIntersection",
            ),
            assignments,
            assigned: assignments.length > 0,
        });
    });
    result.sort((left, right) => (
        Number(left.assigned) - Number(right.assigned)
        || right.effectiveFullyContainedCount - left.effectiveFullyContainedCount
        || right.effectivePartialIntersectionCount - left.effectivePartialIntersectionCount
        || left.label.localeCompare(right.label)
        || left.itemId - right.itemId
    ));
    return Object.freeze(result);
}

export function filterCalculatorRankingEvidenceRows(rows, query) {
    const needle = String(query ?? "").trim().toLocaleLowerCase();
    if (!needle) {
        return rows;
    }
    return Object.freeze(rows.filter((row) => (
        row.label.toLocaleLowerCase().includes(needle)
        || String(row.itemId).includes(needle)
        || row.assignments.some((assignment) => (
            assignment.groupLabel.toLocaleLowerCase().includes(needle)
            || assignment.groupId.toLocaleLowerCase().includes(needle)
            || assignment.layerName.toLocaleLowerCase().includes(needle)
            || String(assignment.sourceName ?? "").toLocaleLowerCase().includes(needle)
            || String(assignment.sourceId ?? "").toLocaleLowerCase().includes(needle)
        ))
        || row.observations.some((observation) => (
            observation.layerName.toLocaleLowerCase().includes(needle)
            || String(observation.sourceName ?? "").toLocaleLowerCase().includes(needle)
            || String(observation.sourceId ?? "").toLocaleLowerCase().includes(needle)
            || observation.status.toLocaleLowerCase().includes(needle)
        ))
    )));
}

export const CALCULATOR_RANKING_OBSERVATION_SCOPES = RANKING_SCOPES;
